import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDiscoveryClient } from '../api/context';
import { leadsKey } from './leads';
import type {
  CampaignCreateInput,
  CampaignLive,
  CampaignLogEntry,
  CampaignPoll,
  CampaignResultEntry,
  CampaignStatus,
} from '../api/types';

export const campaignsKey = ['discovery', 'campaigns'] as const;

export function useCampaigns() {
  const client = useDiscoveryClient();
  return useQuery({
    queryKey: campaignsKey,
    queryFn: () => client.campaigns.list(),
    staleTime: 10_000,
    refetchInterval: 5_000, // refresh list while user is on screen
  });
}

export function useCreateCampaign() {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CampaignCreateInput) => client.campaigns.create(input),
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: campaignsKey });
      qc.invalidateQueries({ queryKey: leadsKey(input.folderId) });
    },
  });
}

export function useStopCampaign() {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.campaigns.stop(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: campaignsKey }),
  });
}

export interface CampaignLiveState {
  status: CampaignStatus | 'idle';
  logs: CampaignLogEntry[];
  sent: number;
  failed: number;
  currentIndex: number;
  totalLeads: number;
  results: Record<string, CampaignResultEntry>;
  error: string | null;
}

const EMPTY: CampaignLiveState = {
  status: 'idle',
  logs: [],
  sent: 0,
  failed: 0,
  currentIndex: 0,
  totalLeads: 0,
  results: {},
  error: null,
};

/**
 * Polls /api/campaigns/:id/poll on a 1.5s tick while running, accumulating
 * logs and tracking counters. Stops once status is done/stopped/error.
 */
export function useCampaignPoll(
  campaignId: string | null,
  initial: CampaignLive | null,
  folderPublicId: string | null,
): CampaignLiveState {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  const [state, setState] = useState<CampaignLiveState>(() => seed(initial));
  const cursor = useRef(initial?.logs?.length ?? 0);
  const seenId = useRef<string | null>(null);

  useEffect(() => {
    if (campaignId !== seenId.current) {
      seenId.current = campaignId;
      cursor.current = initial?.logs?.length ?? 0;
      setState(seed(initial));
    }
  }, [campaignId, initial]);

  useEffect(() => {
    if (!campaignId) return;
    if (state.status === 'done' || state.status === 'stopped' || state.status === 'error') return;
    let cancelled = false;

    async function tick() {
      if (cancelled || !campaignId) return;
      try {
        const poll: CampaignPoll = await client.campaigns.poll(campaignId, cursor.current);
        if (cancelled) return;
        cursor.current += poll.logs.length;
        setState((prev) => ({
          ...prev,
          status: poll.status,
          logs: poll.logs.length ? [...prev.logs, ...poll.logs] : prev.logs,
          sent: poll.sent,
          failed: poll.failed,
          currentIndex: poll.currentIndex,
          totalLeads: poll.totalLeads,
          results: poll.results,
          error: null,
        }));
        if (poll.status === 'done' || poll.status === 'stopped' || poll.status === 'error') {
          qc.invalidateQueries({ queryKey: campaignsKey });
          if (folderPublicId) qc.invalidateQueries({ queryKey: leadsKey(folderPublicId) });
          return;
        }
      } catch (err) {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          status: 'error',
          error: err instanceof Error ? err.message : 'Polling failed',
        }));
        return;
      }
      setTimeout(tick, 1500);
    }

    tick();
    return () => {
      cancelled = true;
    };
  }, [campaignId, client, folderPublicId, qc, state.status]);

  return state;
}

function seed(initial: CampaignLive | null): CampaignLiveState {
  if (!initial) return EMPTY;
  return {
    status: initial.status,
    logs: initial.logs ?? [],
    sent: initial.sent,
    failed: initial.failed,
    currentIndex: initial.currentIndex,
    totalLeads: initial.totalLeads,
    results: initial.results ?? {},
    error: null,
  };
}
